import { ExpandMore, Info } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  FormControlLabel,
  Checkbox,
  Switch,
  DialogActions,
  Dialog,
  DialogContent,
  DialogContentText,
  Button,
  DialogTitle,
  RadioGroup,
  Stack,
  Paper,
  Radio,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accordionProps } from './elements';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import {
  FormStateDataType,
  SelectedGateways,
} from 'containers/dashboard/Forms/FormListingType';
import RazorpayFormSettings from './RazorpayFormSettings';
import { useEffect, useState } from 'react';
import axiosInstance from 'utils/axios';
import { useSelector } from 'react-redux';
import usePortalId from 'hooks/portal/usePortalId';
import { useSnackbar } from 'notistack';

interface IPaymentSettingProps {
  selectedGateways: SelectedGateways[];
  allowDonation: boolean;
  onChangeMode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  allowSubscription: boolean;
  changeKeyValue: (name: string, value: boolean) => void;
  state: FormStateDataType;
  allowRecurringForDonation: boolean;
  setPublishFormData: React.Dispatch<React.SetStateAction<any>>;
  publishFormData: any;
  setProductLoading: React.Dispatch<React.SetStateAction<boolean>>;
  productLoading: boolean;
  setFormUpdated: React.Dispatch<React.SetStateAction<number>>;
  formUpdated: number;
  formData: any;
  stripeOptionInitial: boolean;
  ewalletStripeInitial: boolean;
}

export default function PaymentTabSettings({
  selectedGateways,
  allowDonation,
  onChangeMode,
  allowSubscription,
  changeKeyValue,
  state,
  allowRecurringForDonation,
  setPublishFormData,
  setFormUpdated,
  publishFormData,
  formUpdated,
  formData,
  stripeOptionInitial,
  ewalletStripeInitial,
}: IPaymentSettingProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { formDetails } = useSelector((state: any) => state.formEditor);
  const portalId = usePortalId();
  const [open, setOpen] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const showPreview =
    (publishFormData && formUpdated !== 0) ||
    (state.selectedHubspotFormId && formData.hubspot_form_id !== '',
    formData.hubspot_form_id !== '');

  const razorpayIsSelected =
    selectedGateways.findIndex(
      gateway => gateway.name === 'razorpay' && gateway.is_selected
    ) !== -1;

  const atLeastOneGatewaySelected =
    selectedGateways.findIndex(gateway => gateway.is_selected) !== -1;

  function checkForKeyValueFalse(methods: SelectedGateways[]) {
    for (let i = 0; i < methods.length; i++) {
      if (methods[i].is_selected && methods[i].name === 'stripe') {
        return true;
      }
    }

    return false;
  }
  const stripeTrue = checkForKeyValueFalse(selectedGateways);

  const handleClose = (flagForSubscription: boolean) => {
    if (flagForSubscription) {
      changeKeyValue('allowRecurringForDonation', flagForSubscription);
      updateSubscriptioninForm(flagForSubscription);
    } else {
      changeKeyValue('allowRecurringForDonation', flagForSubscription);
      updateSubscriptioninForm(flagForSubscription);
    }
    setOpen(false);
  };

  // function to enable e-Wallet option in stripe
  const enableWalletOption = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const temp = event.target.checked;
    if (temp) {
      changeKeyValue('enableStripeWallet', true);
    } else {
      changeKeyValue('enableStripeWallet', false);
    }
    try {
      const response = await axiosInstance.post(
        '/formpayhome/portal/updatePortalFormSettings/' + formDetails.formId,
        {
          portal_id: portalId,
          enableStripeWallet: temp,
        }
      );
      if (response.data.success) {
        if (showPreview) {
          setPublishFormData(response.data);
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  const handleStripeCheckoutOptions = async (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const stripeTypeValue = value;
    if (stripeTypeValue === 'stripe_session') {
      changeKeyValue('useStripeCheckoutSession', true);
    } else {
      changeKeyValue('useStripeCheckoutSession', false);
    }
    try {
      const response = await axiosInstance.post(
        '/formpayhome/portal/updatePortalFormSettings/' + formDetails.formId,
        {
          portal_id: portalId,
          useStripeCheckoutSession:
            stripeTypeValue === 'stripe_session' ? true : false,
        }
      );
      if (response.data.success) {
        if (showPreview) {
          setPublishFormData(response.data);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateSubscriptioninForm = async (flagForAllowRecurring: boolean) => {
    try {
      const response = await axiosInstance.post(
        '/formpayhome/portal/updatePortalFormSettings/' + formDetails.formId,
        {
          portalId: portalId,
          form_id: formDetails.formId,
          donation_form_settings: {
            allow_donation: state.allowDonation,
            allowRecurringForDonation: flagForAllowRecurring,
            donation_form_title: state.allowDonation ? state.title : '',
            donation_form_description: state.allowDonation
              ? state.description
              : '',
            donation_form_image: state.allowDonation ? state.logo : '',
            allow_custom_amount: state.allowDonation
              ? state.allowCustomAmount
              : false,
            amount:
              state.allowDonation && state.allowCustomAmount
                ? state.customAmount
                : 0,
            suggested_amount:
              state.allowDonation && state.allowCustomAmount
                ? state.suggestedAmount
                : [],
            show_donation_memory: state.allowDonation
              ? state.showDonationMemory
              : false,
            donation_memory_label:
              state.allowDonation && state.showDonationMemory
                ? state.donationMemoryLabel
                : '',

            donate_button_text: state.donateButtonText,
            transaction_cost_setting: {
              allow_transaction_cost: state.allowTransactionCost,
              transaction_cost_label: state.allowTransactionCost
                ? state.transactionCostLabel
                : '',
              transaction_cost_type: state.allowTransactionCost
                ? state.transactionCostType
                : 'fixed',

              transaction_cost_fixed_amount:
                state.allowTransactionCost &&
                state.transactionCostType === 'fixed'
                  ? state.transactionAmount
                  : 0,
              transaction_cost_variable_base_amount:
                state.allowTransactionCost &&
                state.transactionCostType === 'variable'
                  ? state.transactionCostVariableBaseAmount
                  : 0,
              transaction_cost_variable_unit_amount:
                state.allowTransactionCost &&
                state.transactionCostType === 'variable'
                  ? state.transactionUnitAmount
                  : 0,
            },
          },
          allowSubscription: flagForAllowRecurring,
        }
      );
      if (response.data.success) {
        if (showPreview) {
          setPublishFormData(response.data);
        }
      }
    } catch (err: unknown) {
      console.log(err);
    }
  };
  useEffect(() => {
    if (!allowDonation) {
      changeKeyValue('allowSubscription', false);
    }
  }, [allowDonation, allowRecurringForDonation]);

  useEffect(() => {
    if (!allowSubscription) {
      changeKeyValue('allowOnlyRecurringPayments', false);
    }
  }, [allowSubscription]);

  useEffect(() => {
    // @ts-ignore
    if (publishFormData && window.showPreviewForForm) {
      setFormUpdated(value => value + 1);
    }
  }, [publishFormData, setFormUpdated]);

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Accordion defaultExpanded square={false} sx={accordionProps}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography
              sx={{ marginBottom: '5px' }}
              variant='subtitle2'
              color='text secondary'
            >
              {t('payment_settings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!atLeastOneGatewaySelected && (
              <div>{t('select_gateways_to_see_settings')}</div>
            )}
            {razorpayIsSelected && <RazorpayFormSettings />}
            {!razorpayIsSelected && <div>{t('no_settings_available')}</div>}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded square={false} sx={accordionProps}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography
              sx={{ marginBottom: '5px' }}
              variant='subtitle2'
              color='text secondary'
            >
              {t('subscription_settings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!allowDonation && (
              <div className='flex items-center text-[14px] text-gray-600 mb-3'>
                <Info sx={{ width: '15px', height: '15px' }} />{' '}
                <span className='ml-1'>
                  {t('allow_donation_to_enable_subscription')}
                </span>
              </div>
            )}
            {!stripeTrue && (
              <div className='flex items-center text-[14px] text-gray-600 mb-3'>
                <Info sx={{ width: '15px', height: '15px' }} />{' '}
                <span className='ml-1'>
                  {t('select_stipe_to_enable_subscription')}
                </span>
              </div>
            )}

            <FormControlLabel
              control={
                <Switch
                  name='allowSubscription'
                  disabled={!allowDonation || !stripeTrue}
                  checked={allowRecurringForDonation}
                  onChange={() => {
                    setOpen(true);
                  }}
                />
              }
              label={t('enable_subscription')}
            />
          </AccordionDetails>
        </Accordion>

        {stripeTrue && (
          <>
            <Accordion defaultExpanded square={false} sx={accordionProps}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography
                  sx={{ marginBottom: '5px' }}
                  variant='subtitle2'
                  color='text secondary'
                >
                  {t('stripe_settings')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <RadioGroup
                    className='flex flex-row justify-between'
                    aria-labelledby='stripe_options'
                    name='controlled-radio-buttons-group'
                    value={
                      stripeOptionInitial ? 'stripe_session' : 'stripe_card'
                    }
                    onChange={handleStripeCheckoutOptions}
                  >
                    <Paper variant='outlined' className='w-[45%] p-1'>
                      <FormControlLabel
                        value='stripe_card'
                        control={
                          <Radio
                            className='ml-2'
                            checkedIcon={<CheckCircleIcon />}
                          />
                        }
                        label={t('Scard')}
                      />
                    </Paper>
                    <Paper variant='outlined' className='w-[45%] p-1'>
                      <FormControlLabel
                        value='stripe_session'
                        control={
                          <Radio
                            className='ml-2'
                            checkedIcon={<CheckCircleIcon />}
                          />
                        }
                        label={t('Stripe_Session')}
                      />
                    </Paper>
                  </RadioGroup>
                  <Typography variant='subtitle2' color='text secondary'>
                    {t('e_wallet_setting')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ewalletStripeInitial}
                        onChange={enableWalletOption}
                      />
                    }
                    label={t('enable_apple_gpay_link')}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </motion.div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby='responsive-dialog-title'
      >
        <DialogTitle id='alert-dialog-title'>{t('surety_notice')}</DialogTitle>
        <DialogContent className='mt-3'>
          <DialogContentText>
            {allowSubscription
              ? t('disable_recurring_notice')
              : t('enable_recurring_notice')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              allowRecurringForDonation
                ? handleClose(false)
                : handleClose(true);
            }}
          >
            {t('continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
